"use client";

import { Button } from "@/components/ui/button";
import { ChevronLeft } from "lucide-react";
import Link from "next/link";
import { useUser } from "@/shared/contexts/UserContext";

export default function TermsOfUsePage() {
  const { isAuthenticated } = useUser();

  return (
    <div className="min-h-screen bg-cream">
      <header className="flex justify-between items-center p-4">
        <Link
          href="/"
          className="font-patrick text-2xl md:text-3xl text-charcoal font-bold"
        >
          <div
            className="font-winky text-3xl text-charcoal font-black p-0 m-0"
          >
            <img src="/logo.png" alt="Hey Hoku" width={256} height={263} className="h-10 w-auto" />
          </div>
        </Link>

        <div className="flex items-center gap-2">
          {isAuthenticated ? (
            <Button
              asChild
              className="button-3d rounded-lg bg-[#ffd372] text-charcoal hover:bg-yellow hover:text-black delay-200 ease-initial font-winky text-sm px-5 py-2 border-1 border-[#4c4c4c] transition-all duration-200 transform hover:scale-105 active:scale-95 active:translate-y-[2px] shadow-[2px_2px_0_#4c4c4c]"
            >
              <Link href="/user/dashboard">Dashboard</Link>
            </Button>
          ) : (
            <>
              <Button
                asChild
                className="button-3d rounded-lg bg-[#ffd372] text-charcoal hover:bg-yellow hover:text-black delay-200 ease-initial font-winky text-sm px-5 py-2 border-1 border-[#4c4c4c] transition-all duration-200 transform hover:scale-105 active:scale-95 active:translate-y-[2px] shadow-[2px_2px_0_#4c4c4c]"
              >
                <Link href="/login?action=signup">Sign Up</Link>
              </Button>
              <Button
                asChild
                className="button-3d rounded-lg bg-[#ffd372] text-charcoal hover:bg-yellow hover:text-black delay-200 ease-initial font-winky text-base px-5 py-2 border-1 border-[#4c4c4c] transition-all duration-200 transform hover:scale-105 active:scale-95 active:translate-y-[2px] shadow-[2px_2px_0_#4c4c4c]"
              >
                <Link href="/login">Login</Link>
              </Button>
            </>
          )}
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-3xl p-8 shadow-lg border-2 border-[#ffd372]">
          <h1 className="font-winky text-4xl font-bold text-charcoal mb-8">
            Terms of Use
          </h1>

          <div className="prose prose-lg max-w-none font-winky text-charcoal space-y-6">
            <p className="text-sm text-gray-600 mb-6">
              Effective Date: August 18, 2025
            </p>

            <p>
              This Agreement ("Terms of Use"), governs your use of the Hey Hoku
              application and related services ("Services") provided by Lilia
              Fashion House Private Limited ("Company," "we," "us," "our").
              Please read these Terms of Use and all referenced policies
              carefully before accessing or using any of our Services.
            </p>

            <div className="bg-[#ffd372] p-4 rounded-xl mb-6">
              <h2 className="font-bold text-lg mb-2">Company Information:</h2>
              <p>
                Lilia Fashion House Private Limited
                <br />
                28, Boopathi Nagar, Opp. Central Park Apartments,
                <br />
                Saravanampatti, Coimbatore, Tamil Nadu - 641 035
                <br />
                Contact: swethaa@heyhoku.ai / lilia@liliafashionhouse.com
              </p>
            </div>

            <h2 className="font-bold text-2xl mt-8 mb-4">
              1. GENERAL CONDITIONS AND ACCEPTANCE
            </h2>
            <p>
              1.1. By accessing or using the Services, you agree to be bound by
              these Terms of Use, our Privacy Policy, and the Cookie Policy
              (collectively, the "Agreement").
            </p>
            <p>
              1.2. You may not use the Services if you do not agree to the
              Agreement.
            </p>
            <p>
              1.3. You confirm that you are fully capable and legally competent
              to enter into this Agreement under applicable laws.
            </p>
            <p>
              1.4. This Agreement remains valid for all use of the Services,
              subject to provisions herein regarding updates and termination.
            </p>

            <h2 className="font-bold text-2xl mt-8 mb-4">
              2. OBJECT OF THE AGREEMENT
            </h2>
            <p>
              2.1. The Services consist of a digital wardrobe and personal
              styling assistant powered by AI.
            </p>
            <p>2.2. Features include:</p>
            <ul className="list-disc ml-6 space-y-2">
              <li>Organization of wardrobe items via photo uploads</li>
              <li>
                Personalized styling suggestions based on your preferences and
                inputs
              </li>
              <li>
                Option to share created content externally to social media
                platforms
              </li>
            </ul>
            <p>2.3. Some features require a paid monthly subscription.</p>
            <p>
              2.4. In the future, integration with retailer partners for
              shopping referrals and commissions may become available, subject
              to notice and further agreement.
            </p>

            <h2 className="font-bold text-2xl mt-8 mb-4">
              3. REGISTRATION AND ELIGIBILITY
            </h2>
            <p>
              3.1. To use some features, you may need to register for an
              account, providing: name, email address, and other requisite
              information.
            </p>
            <p>
              3.2. No age restrictions are placed on registration, but users
              under the age required by law in their jurisdiction should obtain
              parental consent.
            </p>
            <p>3.3. Only one account per individual is permitted.</p>
            <p>
              3.4. You are responsible for all activity and content associated
              with your account and must maintain the confidentiality of your
              credentials.
            </p>
            <p>
              3.5. You must provide truthful, accurate, and complete
              registration information and keep it updated.
            </p>
            <p>
              3.6. We reserve the right to refuse registration, or terminate
              accounts for violations, including submission of false or
              misleading information.
            </p>

            <h2 className="font-bold text-2xl mt-8 mb-4">
              4. SUBSCRIPTION AND PAYMENT TERMS
            </h2>
            <p>
              4.1. Hey Hoku offers subscription-based premium features on a
              monthly renewal.
            </p>
            <p>
              4.2. Subscriptions are billed in advance and include applicable
              taxes. All payments are processed via Razorpay.
            </p>
            <p className="font-bold bg-yellow/20 p-3 rounded-lg">
              4.3. YOU UNDERSTAND AND AGREE THAT ALL PAYMENTS ARE FINAL AND
              NON-REFUNDABLE. NO REFUNDS OR PRORATED CREDITS WILL BE PROVIDED
              FOR CANCELLATION OR UNUSED PORTIONS.
            </p>
            <p>
              4.4. Cancellations take effect at the end of the current billing
              cycle.
            </p>
            <p>
              4.5. Pricing and features may change with further notice;
              continued use following changes constitutes acceptance.
            </p>

            <h2 className="font-bold text-2xl mt-8 mb-4">5. USER CONTENT</h2>
            <p>
              5.1. Users may upload, share, or create content such as photos,
              styling preferences, and wardrobe data ("User Content").
            </p>
            <p>
              5.2. By posting User Content, you grant us a worldwide,
              non-exclusive, royalty-free, transferable, sublicensable license
              to use, display, modify, and distribute your content only for the
              purposes of providing and promoting the Services and as otherwise
              outlined in the Privacy Policy.
            </p>
            <p>5.3. You retain ownership of your User Content.</p>
            <p>
              5.4. You must not post or share any User Content that infringes
              upon third-party rights, is illegal, or breaches these Terms of
              Use.
            </p>
            <p>
              5.5. All content must comply with community standards and
              applicable law.
            </p>
            <p>
              5.6. User Content is not shared within the app's social feed;
              sharing is available only to external platforms at your
              discretion.
            </p>

            <h2 className="font-bold text-2xl mt-8 mb-4">6. RESTRICTED USES</h2>
            <p>
              6.1. The Services must not be used for any unlawful purposes, to
              harm other users or third parties, or to damage or impair the
              operation of Hey Hoku.
            </p>
            <p>6.2. Prohibited actions include:</p>
            <ul className="list-disc ml-6 space-y-2">
              <li>Misrepresentation or impersonation</li>
              <li>Distribution of unsolicited content, spam, or malware</li>
              <li>
                Violation of intellectual property, privacy, publicity or legal
                rights of others
              </li>
              <li>
                Harassment, abusive, defamatory, or offensive communications
              </li>
              <li>Unauthorized access or interference with the Services</li>
            </ul>

            <h2 className="font-bold text-2xl mt-8 mb-4">
              12. GOVERNING LAW AND JURISDICTION
            </h2>
            <p>
              12.1. These terms shall be governed by and construed in accordance
              with the laws of India. Disputes arise out of or relating to these
              terms shall be subject to the exclusive jurisdiction of courts in
              Coimbatore, Tamil Nadu.
            </p>

            <h2 className="font-bold text-2xl mt-8 mb-4">
              13. CONTACT INFORMATION
            </h2>
            <p>
              For support, claims, or legal questions:
              <br />
              swethaa@heyhoku.ai / lilia@liliafashionhouse.com
            </p>
          </div>

          <nav className="mt-16 border-t border-[#eee] pt-8">
            <p className="font-winky text-[13px] text-[#767676] mb-3">Related policies</p>
            <div className="flex flex-wrap gap-4">
              <Link href="/legal/privacy" className="font-winky text-[14px] text-[#2d2d2d] underline decoration-[#ffd372] decoration-2 underline-offset-2 hover:text-[#c0392b] transition-colors">Privacy Policy</Link>
              <Link href="/legal/cookies" className="font-winky text-[14px] text-[#2d2d2d] underline decoration-[#ffd372] decoration-2 underline-offset-2 hover:text-[#c0392b] transition-colors">Cookie Policy</Link>
              <Link href="/support" className="font-winky text-[14px] text-[#2d2d2d] underline decoration-[#ffd372] decoration-2 underline-offset-2 hover:text-[#c0392b] transition-colors">Contact Support</Link>
            </div>
          </nav>
        </div>
      </div>
    </div>
  );
}
